import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download } from 'lucide-react';

import { apiClient } from '../lib/api.js';
import type { TalentDetail } from '@arx-marketplace/shared/talents';
import type { ShowcasePayload } from '@arx-marketplace/shared/showcase';
import { cn } from '../lib/utils.js';

export function TalentPage(): JSX.Element {
  const { slug = '' } = useParams();
  const [data, setData] = useState<TalentDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .getTalent(slug)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="container py-12 text-destructive">{err}</div>;
  if (!data) return <div className="container py-12 text-muted-foreground">Loading…</div>;

  return (
    <article className="container py-10 flex-1 max-w-4xl">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{data.name}</h1>
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <span className="px-1.5 py-0.5 rounded bg-muted">{data.category}</span>
            {data.latest_version && <code>v{data.latest_version}</code>}
            {data.owner_name && <span>· by {data.owner_name}</span>}
          </div>
          <p className="mt-3 text-muted-foreground">{data.summary}</p>
        </div>
        {data.latest_version && (
          <a
            href={apiClient.downloadUrl(data.slug)}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-md',
              'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors',
            )}
          >
            <Download className="h-4 w-4" />
            Download .tulpa
          </a>
        )}
      </header>

      {data.showcase.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xl font-semibold">Showcase</h2>
          <div className="mt-4 space-y-4">
            {data.showcase.map((s) => (
              <ShowcaseBlock key={s.id} payload={s.payload} />
            ))}
          </div>
        </section>
      )}

      {data.description && (
        <section className="mt-10">
          <h2 className="text-xl font-semibold">About</h2>
          <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed">
            {data.description}
          </pre>
        </section>
      )}

      <section className="mt-10">
        <h2 className="text-xl font-semibold">Versions</h2>
        <table className="mt-4 w-full text-sm">
          <thead className="text-muted-foreground">
            <tr>
              <th className="text-left font-normal py-2">Version</th>
              <th className="text-left font-normal py-2">Size</th>
              <th className="text-left font-normal py-2">SHA-256</th>
              <th className="text-left font-normal py-2">Released</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.versions.map((v) => (
              <tr key={v.id} className="border-t">
                <td className="py-2">
                  <code>{v.version}</code>
                </td>
                <td className="py-2 text-muted-foreground">{formatBytes(v.size_bytes)}</td>
                <td className="py-2 text-muted-foreground">
                  <code className="text-xs">{v.sha256.slice(0, 12)}…</code>
                </td>
                <td className="py-2 text-muted-foreground">
                  {new Date(v.created_at).toLocaleDateString()}
                </td>
                <td className="py-2 text-right">
                  <a
                    href={apiClient.downloadUrl(data.slug, v.version)}
                    className="text-sm underline-offset-4 hover:underline"
                  >
                    download
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </article>
  );
}

function ShowcaseBlock({ payload }: { payload: ShowcasePayload }): JSX.Element {
  switch (payload.kind) {
    case 'text':
      return <p className="text-sm leading-relaxed">{payload.body}</p>;
    case 'md':
      return (
        <pre className="rounded-md border bg-card p-4 text-sm whitespace-pre-wrap font-sans">
          {payload.body}
        </pre>
      );
    case 'image':
      return (
        <figure>
          <img
            src={`/api/r2/${payload.r2_key}`}
            alt={payload.alt ?? ''}
            className="rounded-md border max-w-full"
          />
          {payload.alt && (
            <figcaption className="mt-2 text-xs text-muted-foreground">{payload.alt}</figcaption>
          )}
        </figure>
      );
    case 'pptx':
      return (
        <div className="rounded-md border p-4 bg-card">
          <p className="text-sm">
            PPTX preview ({payload.page_count ?? '?'} pages){' '}
            <a
              href={`/api/r2/${payload.r2_key}`}
              className="underline underline-offset-4 ml-2"
              download
            >
              download
            </a>
          </p>
        </div>
      );
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
